import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/api';
import FinanceTabBar from '../components/finance/FinanceTabBar';
import { PageHeroBand } from '../components/ui';
import toast from 'react-hot-toast';
import { CURRENCY } from '../config/currency';
import { useConfirm } from '../context/ConfirmContext';

/**
 * Tips distribution (UK plan Phase 4 — Employment (Allocation of Tips) Act
 * 2023). GB vendors only (route + footer link are gated by the
 * `tipsDistribution` feature flag). One compact page: monthly pot →
 * allocation preview → immutable allocation → per-worker payout tracking,
 * plus the allocation rule, tronc role points and the written policy.
 */

interface PotResponse {
  period: string;
  serviceCharge: number;
  cardTips: number;
  total: number;
}

interface AllocationLine {
  id?: number;
  staffMemberId: number;
  staffName: string;
  staffRole?: string;
  weight: number;
  amount: number;
  paid?: boolean;
  paidAt?: string;
}

interface RunResponse {
  id: number;
  period: string;
  allocationRule: string;
  potServiceCharge: number;
  potCardTips: number;
  potTotal: number;
  status: string;
  lines: AllocationLine[];
}

interface SettingsResponse {
  allocationRule: string;
  policyText?: string;
  rolePoints: Record<string, number>;
}

const RULES: Array<{ key: string; label: string; hint: string }> = [
  { key: 'EQUAL', label: 'Equal split', hint: 'Everyone active gets the same share' },
  { key: 'HOURS_WEIGHTED', label: 'Hours worked', hint: 'Weighted by attendance minutes this month' },
  { key: 'ROLE_POINTS', label: 'Role points', hint: 'Tronc-style points per role' },
];

function lastMonths(count: number): string[] {
  const months: string[] = [];
  const d = new Date();
  for (let i = 0; i < count; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    months.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

const VendorTips: React.FC = () => {
  const confirm = useConfirm();
  const [period, setPeriod] = useState(lastMonths(2)[1]); // default: previous month (the payable one)
  const [pot, setPot] = useState<PotResponse | null>(null);
  const [run, setRun] = useState<RunResponse | null>(null);
  const [preview, setPreview] = useState<AllocationLine[] | null>(null);
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [policyDraft, setPolicyDraft] = useState('');
  const [pointsDraft, setPointsDraft] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [potRes, runsRes, settingsRes] = await Promise.all([
        api.get(`/api/vendor/finance/tips/pot?period=${period}`),
        api.get('/api/vendor/finance/tips/runs'),
        api.get('/api/vendor/finance/tips/settings'),
      ]);
      setPot(potRes.data);
      setSettings(settingsRes.data);
      setPolicyDraft(settingsRes.data?.policyText || '');
      setPointsDraft(settingsRes.data?.rolePoints || {});

      const existing = (runsRes.data as RunResponse[]).find((r) => r.period === period);
      if (existing) {
        const detail = await api.get(`/api/vendor/finance/tips/runs/${existing.id}`);
        setRun(detail.data);
        setPreview(null);
      } else {
        setRun(null);
        const p = await api.get(`/api/vendor/finance/tips/preview?period=${period}`);
        setPreview(p.data.lines);
      }
    } catch (e) {
      console.error('Failed to load tips data', e);
      toast.error('Failed to load tips data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const saveRule = async (rule: string) => {
    try {
      const res = await api.put('/api/vendor/finance/tips/settings', { allocationRule: rule });
      setSettings(res.data);
      if (!run) {
        const p = await api.get(`/api/vendor/finance/tips/preview?period=${period}&rule=${rule}`);
        setPreview(p.data.lines);
      }
    } catch {
      toast.error('Failed to save rule');
    }
  };

  const savePolicy = async () => {
    try {
      await api.put('/api/vendor/finance/tips/settings', { policyText: policyDraft });
      toast.success('Policy saved');
    } catch {
      toast.error('Failed to save policy');
    }
  };

  const savePoints = async () => {
    try {
      await api.put('/api/vendor/finance/tips/settings', { rolePoints: pointsDraft });
      toast.success('Role points saved');
      if (!run) {
        const p = await api.get(`/api/vendor/finance/tips/preview?period=${period}&rule=ROLE_POINTS`);
        setPreview(p.data.lines);
      }
    } catch {
      toast.error('Failed to save role points');
    }
  };

  const finalize = async () => {
    if (!(await confirm({
      title: 'Allocate tips',
      description: `Allocate ${CURRENCY.symbol}${pot?.total?.toFixed(2)} for ${period}? ` +
        'This creates a permanent legal record and cannot be re-run.',
      confirmText: 'Allocate',
      cancelText: 'Cancel',
    }))) return;
    try {
      await api.post('/api/vendor/finance/tips/finalize', { period, rule: settings?.allocationRule });
      toast.success(`Tips for ${period} allocated`);
      load();
    } catch (e) {
      const msg = (e as { response?: { data?: { userMessage?: string; message?: string } } })
          ?.response?.data;
      toast.error(msg?.userMessage || msg?.message || 'Failed to allocate');
    }
  };

  const markPaid = async (lineId?: number) => {
    if (!lineId) return;
    try {
      await api.post(`/api/vendor/finance/tips/lines/${lineId}/mark-paid`, {});
      toast.success('Marked as paid');
      load();
    } catch {
      toast.error('Failed to mark as paid');
    }
  };

  const sym = CURRENCY.symbol;
  const previewRoles = Array.from(new Set(
      (preview || run?.lines || []).map((l) => l.staffRole).filter(Boolean))) as string[];

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      <div>
        <FinanceTabBar />
        {/* Header — confident-shell hero band */}
        <PageHeroBand
          eyebrow="Finance · Tips"
          title="Tips Distribution"
          heroLabel="To distribute"
          heroValue={`${sym}${(pot?.total ?? 0).toFixed(2)}`}
        />
        {/* Statutory note — kept verbatim (Employment (Allocation of Tips) Act 2023) */}
        <p className="text-sm text-gray-500 mt-1">
          100% of tips and service charges must reach staff, no later than the end of the month
          after they were received (Employment (Allocation of Tips) Act 2023).
        </p>
      </div>

      {/* Month + pot */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">Tips pot</h2>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
          >
            {lastMonths(12).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3">
            <p className="text-xs text-gray-500">Service charges</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{sym}{(pot?.serviceCharge ?? 0).toFixed(2)}</p>
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3">
            <p className="text-xs text-gray-500">Card tips</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{sym}{(pot?.cardTips ?? 0).toFixed(2)}</p>
          </div>
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 border border-amber-200 dark:border-amber-700">
            <p className="text-xs text-amber-700 dark:text-amber-400">To distribute</p>
            <p className="text-lg font-bold text-amber-800 dark:text-amber-300">{sym}{(pot?.total ?? 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Allocation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            Allocation {run && (
              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                run.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                {run.status === 'PAID' ? 'Fully paid' : 'Allocated — payouts pending'}
              </span>
            )}
          </h2>
        </div>

        {!run && (
          <div className="flex flex-wrap gap-3 mb-4">
            {RULES.map((r) => (
              <label key={r.key} className={`flex-1 min-w-[160px] p-3 rounded-lg border cursor-pointer ${
                settings?.allocationRule === r.key
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
                  : 'border-gray-200 dark:border-gray-600'}`}>
                <input
                  type="radio"
                  className="sr-only"
                  checked={settings?.allocationRule === r.key}
                  onChange={() => saveRule(r.key)}
                />
                <p className="text-sm font-medium text-gray-900 dark:text-white">{r.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{r.hint}</p>
              </label>
            ))}
          </div>
        )}

        {!run && settings?.allocationRule === 'ROLE_POINTS' && previewRoles.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Points per role</p>
            <div className="flex flex-wrap gap-3">
              {previewRoles.map((role) => (
                <label key={role} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  {role}
                  <input
                    type="number"
                    min={0}
                    value={pointsDraft[role] ?? 0}
                    onChange={(e) => setPointsDraft({ ...pointsDraft, [role]: parseInt(e.target.value) || 0 })}
                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded bg-white dark:bg-gray-700"
                  />
                </label>
              ))}
              <button onClick={savePoints}
                className="px-3 py-1 text-xs font-medium rounded bg-gray-800 text-white hover:bg-gray-700">
                Save points
              </button>
            </div>
          </div>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-100 dark:border-gray-700">
              <th className="py-2">Staff</th>
              <th className="py-2">Role</th>
              <th className="py-2 text-right">Share</th>
              <th className="py-2 text-right">{run ? 'Status' : ''}</th>
            </tr>
          </thead>
          <tbody>
            {(run?.lines || preview || []).map((l) => (
              <tr key={l.staffMemberId} className="border-b border-gray-50 dark:border-gray-700/50">
                <td className="py-2 text-gray-900 dark:text-white">{l.staffName}</td>
                <td className="py-2 text-gray-500">{l.staffRole || '—'}</td>
                <td className="py-2 text-right font-medium text-gray-900 dark:text-white">
                  {sym}{Number(l.amount).toFixed(2)}
                </td>
                <td className="py-2 text-right">
                  {run && (l.paid ? (
                    <span className="text-xs text-green-600 font-medium">Paid ✓</span>
                  ) : (
                    <button onClick={() => markPaid(l.id)}
                      className="px-3 py-1 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700">
                      Mark paid
                    </button>
                  ))}
                </td>
              </tr>
            ))}
            {!loading && (run?.lines || preview || []).length === 0 && (
              <tr><td colSpan={4} className="py-4 text-center text-gray-400 text-sm">
                No staff to allocate to — add staff members under Staff Payments.
              </td></tr>
            )}
          </tbody>
        </table>

        {!run && (pot?.total ?? 0) > 0 && (preview || []).length > 0 && (
          <div className="mt-4 flex justify-end">
            <button onClick={finalize}
              className="px-5 py-2 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600">
              Allocate &amp; lock {period}
            </button>
          </div>
        )}
      </div>

      {/* Written policy */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-1">Written tips policy</h2>
        <p className="text-xs text-gray-500 mb-3">
          The law requires a written policy available to your staff describing how tips are allocated.
        </p>
        <textarea
          value={policyDraft}
          onChange={(e) => setPolicyDraft(e.target.value)}
          rows={4}
          placeholder="e.g. All tips and service charges are pooled monthly and shared between all staff based on hours worked…"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
        />
        <div className="mt-2 flex justify-end">
          <button onClick={savePolicy}
            className="px-4 py-1.5 text-sm font-medium rounded-lg bg-gray-800 text-white hover:bg-gray-700">
            Save policy
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorTips;
